import React from 'react';
import { UserIdentity } from './types';
import { FONT_FAMILIES, getNameStyle } from './styles';

interface IdentityRendererProps {
  user: UserIdentity;
  containerClassName?: string;
  nameClassName?: string;
  pronounClassName?: string;
  isLightPalette?: boolean;
  roleColor?: string;
}

import { getBadgeIcon } from '../../utils/badges';

export const IdentityRenderer: React.FC<IdentityRendererProps> = ({ 
  user, 
  containerClassName = "flex items-baseline gap-3",
  nameClassName = "font-space text-2xl font-black uppercase tracking-tight mb-1 truncate",
  pronounClassName = "text-xs font-semibold uppercase tracking-wider",
  isLightPalette = false,
  roleColor
}) => {
  const customization = user.customization || {};
  
  const fontKey = customization.fontFamily || customization.font || 'Inter';
  const fontFamily = FONT_FAMILIES[fontKey] || FONT_FAMILIES.Inter;
  
  const isAnimatedGradient = customization.nameStyle === 'Gradient' && !!customization.gradientAnimated;
  const nameStyleCSS = getNameStyle(
    customization.nameStyle || 'Solid',
    customization.color1 || '#ffffff',
    customization.color2 || '#ffffff',
    customization.gradientDirection,
    isAnimatedGradient
  );

  if (roleColor) {
    nameStyleCSS.color = roleColor;
    nameStyleCSS.backgroundImage = 'none';
    nameStyleCSS.WebkitBackgroundClip = 'initial';
    nameStyleCSS.WebkitTextFillColor = 'initial';
    if (customization.nameStyle === 'Neon' || customization.nameStyle === 'Glow') {
      nameStyleCSS.textShadow = `0 0 8px ${roleColor}, 0 0 20px ${roleColor}44`;
    }
  }

  const defaultTextColor = isLightPalette ? "#1a1610" : "inherit";
  const mutedTextColor = isLightPalette ? "rgba(26, 22, 16, 0.6)" : "rgba(255, 255, 255, 0.6)";
  
  const badgeIcon = getBadgeIcon(customization.badge);

  return (
    <div className={`${containerClassName} min-w-0 flex flex-wrap items-center`}>
      <span
        className={`${nameClassName.replace('truncate', 'break-words whitespace-pre-wrap')} min-w-0${isAnimatedGradient ? ' name-gradient-live' : ''}`}
        style={{ ...nameStyleCSS, fontFamily, color: nameStyleCSS.color || defaultTextColor }}
      >
        {user.name}
      </span>
      <UserBadge customization={customization} isLightPalette={isLightPalette} className="shrink-0 whitespace-nowrap" />
      {customization.pronouns && (
        <span className={`${pronounClassName} shrink-0`} style={{ color: mutedTextColor }}>
          ({customization.pronouns})
        </span>
      )}
    </div>
  );
};

export const UserBadge: React.FC<{ customization?: any; isLightPalette?: boolean; className?: string }> = ({ customization, isLightPalette = false, className = "" }) => {
  // New freeform format: emoji + custom text (max 6 chars WITH emoji,
  // max 7 chars without). When either is set, this wins over the legacy
  // fixed-name badge.
  const customIcon: string = customization?.customBadgeIcon || '';
  const customText: string = customization?.customBadgeText || '';
  const hasCustom = !!(customIcon || customText);
  // Legacy format: pre-existing ShowcaseBadge value (e.g. "Void Walker").
  // Older profiles use this fallback path. New profiles leave it at the
  // default 'None' and rely on the freeform fields.
  const legacyBadge: string = customization?.badge || '';

  if (!hasCustom && (!legacyBadge || legacyBadge === 'None')) return null;

  // Freeform wins; legacy only renders when freeform is absent. The
  // legacy branch still goes through getBadgeIcon so existing data
  // displays the same emoji it always did.
  const icon = hasCustom ? customIcon : getBadgeIcon(legacyBadge);
  const text = hasCustom ? customText : legacyBadge;
  if (!icon && !text) return null;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold shrink-0 ${className}`}
      style={{
        background: isLightPalette ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
        color: isLightPalette ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)'
      }}
      title={text || ''}
    >
      {icon && <span>{icon}</span>}
      {text && <span>{text}</span>}
    </div>
  );
};
